import React, { useState } from "react";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import { COUNTRY_OPTIONS, CURRENCY_OPTIONS } from "../constants";

const Tab1PersonalProfile = ({
  personalData,
  setPersonalData,
  professionalData,
  setProfessionalData,
  availabilityData,
  setAvailabilityData,
  commercialData,
  setCommercialData,
  errors,
  loading,
  handlePersonalChange,
  handleProfessionalChange,
  handleMultiSelectChange,
  handleAvailabilityChange,
  handleEngagementTypesChange,
  handleCommercialChange,
  roleOptions = [],
  availabilityStatusOptions = [],
  rateTypeOptions = [],
  engagementTypeOptions = [],
  genderOptions = [],
  referenceValuesLoading = false,
  onSubmit
}) => {
  const [expandedSections, setExpandedSections] = useState({
    identity: true,
    professional: true,
    availability: true,
  });

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleNationalityChange = (index, value) => {
    setPersonalData((prev) => {
      const updated = [...prev.nationalities];
      updated[index] = value;
      return { ...prev, nationalities: updated };
    });
  };

  const addNationality = () => {
    setPersonalData((prev) => ({
      ...prev,
      nationalities: [...prev.nationalities, ""],
    }));
  };

  const removeNationality = (index) => {
    setPersonalData((prev) => ({
      ...prev,
      nationalities: prev.nationalities.filter((_, idx) => idx !== index),
    }));
  };

  const handlePreferredLocationChange = (index, value) => {
    setAvailabilityData((prev) => {
      const updated = [...prev.preferred_locations];
      updated[index] = value;
      return { ...prev, preferred_locations: updated };
    });
  };

  const addPreferredLocation = () => {
    setAvailabilityData((prev) => ({
      ...prev,
      preferred_locations: [...prev.preferred_locations, ""],
    }));
  };

  const removePreferredLocation = (index) => {
    setAvailabilityData((prev) => ({
      ...prev,
      preferred_locations: prev.preferred_locations.filter((_, idx) => idx !== index),
    }));
  };

  const selectedAvailabilityStatusLabel =
    availabilityStatusOptions.find(
      (opt) => String(opt.id) === String(availabilityData.status)
    )?.label || "";
  const isAvailableFromDateStatus = selectedAvailabilityStatusLabel === "AVAILABLE_FROM_DATE";
  const isServingNoticeStatus = selectedAvailabilityStatusLabel === "SERVING_NOTICE";

  const handleAvailabilityStatusChange = (e) => {
    handleAvailabilityChange(e);

    const nextOption = availabilityStatusOptions.find(
      (opt) => String(opt.id) === String(e.target.value)
    );
    const nextLabel = nextOption?.label || "";

    setAvailabilityData((prev) => ({
      ...prev,
      available_from: nextLabel === "AVAILABLE_FROM_DATE" ? prev.available_from : "",
      notice_period_days: nextLabel === "SERVING_NOTICE" ? prev.notice_period_days : "",
    }));
  };

  return (
    <div>
      {/* Section 1: Personal Identity */}
      <div className="register-stage2-section">
        <div
          className="register-stage2-section__header"
          onClick={() => toggleSection("identity")}
        >
          <div className="register-stage2-section__header-top">
            <h3 className="register-stage2-section__title">
              <span className="register-stage2-section__icon">👤</span>
              1. Personal Identity
            </h3>
            <span className="register-stage2-section__chevron">
              {expandedSections.identity ? <FaChevronUp /> : <FaChevronDown />}
            </span>
          </div>
          <p className="register-stage2-section__subtitle">
            Identity and document matching information.
          </p>
        </div>

        {expandedSections.identity && (
          <>
            <div className="register-stage2-form__grid register-stage2-form__grid--4col">
              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">
                  Legal Full Name <span className="register-stage2-form__required">*</span>
                </label>
                <input
                  type="text"
                  name="legal_full_name"
                  className={`register-stage2-form__input ${errors.legal_full_name ? "register-stage2-form__input--error" : ""}`}
                  value={personalData.legal_full_name}
                  onChange={handlePersonalChange}
                  placeholder="As per identity document"
                  disabled={loading}
                />
                {errors.legal_full_name && (
                  <span className="register-stage2-form__error-text">{errors.legal_full_name}</span>
                )}
                <small className="register-stage2-form__help-text">
                  Keep separate from display name.
                </small>
              </div>

              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">
                  Professional Display Name <span className="register-stage2-form__required">*</span>
                </label>
                <input
                  type="text"
                  name="display_name"
                  className={`register-stage2-form__input ${errors.display_name ? "register-stage2-form__input--error" : ""}`}
                  value={personalData.display_name}
                  onChange={handlePersonalChange}
                  placeholder="Name used in profile and CV"
                  disabled={loading}
                />
                {errors.display_name && (
                  <span className="register-stage2-form__error-text">{errors.display_name}</span>
                )}
              </div>

              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">Date of Birth</label>
                <input
                  type="date"
                  name="date_of_birth"
                  className="register-stage2-form__input"
                  value={personalData.date_of_birth}
                  onChange={handlePersonalChange}
                  disabled={loading}
                />
                <small className="register-stage2-form__help-text">
                  Optional, never shown by default
                </small>
              </div>

              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">Gender</label>
                <select
                  name="gender"
                  className="register-stage2-form__select"
                  value={personalData.gender}
                  onChange={handlePersonalChange}
                  disabled={loading || referenceValuesLoading}
                >
                  <option value="">Prefer not to say</option>
                  {genderOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
                <small className="register-stage2-form__help-text">
                  Optional, avoid mandatory collection
                </small>
              </div>
            </div>

            {/* Nationality - Full width */}
            <div className="register-stage2-form__grid register-stage2-form__grid--1col">
              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">
                  Nationality / Citizenship <span className="register-stage2-form__required">*</span>
                </label>
                <div className="register-stage2-nationality-list">
                  {personalData.nationalities.map((nationality, index) => (
                    <div key={index} className="register-stage2-nationality-row">
                      <input
                        type="text"
                        className="register-stage2-form__input register-stage2-nationality-input"
                        value={nationality}
                        onChange={(e) => handleNationalityChange(index, e.target.value)}
                        placeholder={index === 0 ? "India (primary)" : "Nationality"}
                        disabled={loading}
                      />
                      {personalData.nationalities.length > 1 && (
                        <button
                          type="button"
                          className="register-stage2-form__btn register-stage2-form__btn--secondary"
                          onClick={() => removeNationality(index)}
                          disabled={loading}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="register-stage2-add-btn register-stage2-add-btn--small"
                    onClick={addNationality}
                    disabled={loading}
                  >
                    + Add another nationality
                  </button>
                </div>
                {errors.nationalities && (
                  <span className="register-stage2-form__error-text">{errors.nationalities}</span>
                )}
                <small className="register-stage2-form__help-text">
                  Supports multiple nationalities — repeatable list, not a single field.
                </small>
              </div>
            </div>

            {/* Residential Address */}
            <div className="register-stage2-form__subgroup">
              <div className="register-stage2-form__subgroup-title">
                Residential address — structured, never on public profile
              </div>
              <div className="register-stage2-form__grid register-stage2-form__grid--4col">
                <div className="register-stage2-form__field-group">
                  <label className="register-stage2-form__label">Address line 1</label>
                  <input
                    type="text"
                    name="address_line_1"
                    className="register-stage2-form__input"
                    value={personalData.address_line_1}
                    onChange={handlePersonalChange}
                    placeholder="Flat / street"
                    disabled={loading}
                  />
                </div>

                <div className="register-stage2-form__field-group">
                  <label className="register-stage2-form__label">Address line 2 <span className="register-stage2-form__help-text">optional</span></label>
                  <input
                    type="text"
                    name="address_line_2"
                    className="register-stage2-form__input"
                    value={personalData.address_line_2}
                    onChange={handlePersonalChange}
                    disabled={loading}
                  />
                </div>

                <div className="register-stage2-form__field-group">
                  <label className="register-stage2-form__label">City</label>
                  <input
                    type="text"
                    name="city"
                    className="register-stage2-form__input"
                    value={personalData.city}
                    onChange={handlePersonalChange}
                    placeholder="Mumbai"
                    disabled={loading}
                  />
                </div>

                <div className="register-stage2-form__field-group">
                  <label className="register-stage2-form__label">Postal / ZIP code</label>
                  <input
                    type="text"
                    name="postal_code"
                    className="register-stage2-form__input"
                    value={personalData.postal_code}
                    onChange={handlePersonalChange}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="register-stage2-form__grid register-stage2-form__grid--4col">
                <div className="register-stage2-form__field-group">
                  <label className="register-stage2-form__label">Country</label>
                  <select
                    name="address_country"
                    className="register-stage2-form__select"
                    value={personalData.address_country}
                    onChange={handlePersonalChange}
                    disabled={loading}
                  >
                    {COUNTRY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="register-stage2-form__grid register-stage2-form__grid--4col">
              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">
                  Personal Email <span className="register-stage2-form__required">*</span>
                </label>
                <input
                  type="email"
                  name="personal_email"
                  className={`register-stage2-form__input ${errors.personal_email ? "register-stage2-form__input--error" : ""}`}
                  value={personalData.personal_email}
                  onChange={handlePersonalChange}
                  placeholder="your.email@example.com"
                  disabled={loading}
                />
                {errors.personal_email && (
                  <span className="register-stage2-form__error-text">{errors.personal_email}</span>
                )}
              </div>

              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">
                  Primary Phone <span className="register-stage2-form__required">*</span>
                </label>
                <input
                  type="tel"
                  name="primary_phone"
                  className={`register-stage2-form__input ${errors.primary_phone ? "register-stage2-form__input--error" : ""}`}
                  value={personalData.primary_phone}
                  onChange={handlePersonalChange}
                  placeholder="+91 98765 43210"
                  disabled={loading}
                />
                {errors.primary_phone && (
                  <span className="register-stage2-form__error-text">{errors.primary_phone}</span>
                )}
              </div>

              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">LinkedIn Profile URL</label>
                <input
                  type="url"
                  name="linkedin_url"
                  className="register-stage2-form__input"
                  value={personalData.linkedin_url}
                  onChange={handlePersonalChange}
                  placeholder="https://linkedin.com/in/yourprofile"
                  disabled={loading}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Section 2: Professional Summary */}
      <div className="register-stage2-section">
        <div
          className="register-stage2-section__header"
          onClick={() => toggleSection("professional")}
        >
          <div className="register-stage2-section__header-top">
            <h3 className="register-stage2-section__title">
              <span className="register-stage2-section__icon">💼</span>
              2. Professional Summary
            </h3>
            <span className="register-stage2-section__chevron">
              {expandedSections.professional ? <FaChevronUp /> : <FaChevronDown />}
            </span>
          </div>
          <p className="register-stage2-section__subtitle">
            Headline, summary, roles and scope experience.
          </p>
        </div>

        {expandedSections.professional && (
          <>
            <div className="register-stage2-form__grid register-stage2-form__grid--1col">
              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">
                  Professional Headline <span className="register-stage2-form__required">*</span>
                </label>
                <input
                  type="text"
                  name="headline"
                  className={`register-stage2-form__input ${errors.headline ? "register-stage2-form__input--error" : ""}`}
                  value={professionalData.headline}
                  onChange={handleProfessionalChange}
                  placeholder="Level 4 – Oil & Gas – Coating Surveyor"
                  disabled={loading}
                />
                {errors.headline && (
                  <span className="register-stage2-form__error-text">{errors.headline}</span>
                )}
                <small className="register-stage2-form__help-text">
                  10–140 characters. Can auto-generate from verified level, industry, scope.
                </small>
              </div>

              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">
                  Professional Summary <span className="register-stage2-form__required">*</span>
                </label>
                <textarea
                  name="summary"
                  className={`register-stage2-form__textarea ${errors.summary ? "register-stage2-form__textarea--error" : ""}`}
                  value={professionalData.summary}
                  onChange={handleProfessionalChange}
                  rows="4"
                  placeholder="500–2,000 characters"
                  disabled={loading}
                />
                {errors.summary && (
                  <span className="register-stage2-form__error-text">{errors.summary}</span>
                )}
              </div>
            </div>

            <div className="register-stage2-form__grid register-stage2-form__grid--4col">
              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">Key strengths <span className="register-stage2-form__help-text">max 12 tags</span></label>
                <input
                  type="text"
                  name="key_strengths"
                  className="register-stage2-form__input"
                  value={professionalData.key_strengths.join(", ")}
                  onChange={handleProfessionalChange}
                  placeholder="Traceability, NCR control, coating inspection"
                  disabled={loading}
                />
              </div>

              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">
                  Primary professional role <span className="register-stage2-form__required">*</span>
                </label>
                <select
                  name="primary_role"
                  className={`register-stage2-form__select ${errors.primary_role ? "register-stage2-form__select--error" : ""}`}
                  value={professionalData.primary_role}
                  onChange={handleProfessionalChange}
                  disabled={loading || referenceValuesLoading}
                >
                  <option value="">{referenceValuesLoading ? "Loading roles..." : "Select role"}</option>
                  {roleOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
                {errors.primary_role && (
                  <span className="register-stage2-form__error-text">{errors.primary_role}</span>
                )}
                <small className="register-stage2-form__help-text">
                  From Role Master (30 defined roles).
                </small>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Section 3: Availability & Commercial */}
      <div className="register-stage2-section">
        <div
          className="register-stage2-section__header"
          onClick={() => toggleSection("availability")}
        >
          <div className="register-stage2-section__header-top">
            <h3 className="register-stage2-section__title">
              <span className="register-stage2-section__icon">📅</span>
              3. Availability & Commercial
            </h3>
            <span className="register-stage2-section__chevron">
              {expandedSections.availability ? <FaChevronUp /> : <FaChevronDown />}
            </span>
          </div>
          <p className="register-stage2-section__subtitle">
            Availability, engagement preferences and commercial details.
          </p>
        </div>

        {expandedSections.availability && (
          <>
            <div className="register-stage2-form__grid register-stage2-form__grid--4col">
              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">
                  Availability status <span className="register-stage2-form__required">*</span>
                </label>
                <select
                  name="status"
                  className={`register-stage2-form__select ${errors.avail_status ? "register-stage2-form__select--error" : ""}`}
                  value={availabilityData.status}
                  onChange={handleAvailabilityStatusChange}
                  disabled={loading || referenceValuesLoading}
                >
                  <option value="">{referenceValuesLoading ? "Loading statuses..." : "Select status"}</option>
                  {availabilityStatusOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
                {errors.avail_status && (
                  <span className="register-stage2-form__error-text">{errors.avail_status}</span>
                )}
              </div>

              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">Available from date <span className="register-stage2-form__help-text">conditional</span></label>
                <input
                  type="date"
                  name="available_from"
                  className="register-stage2-form__input"
                  value={availabilityData.available_from}
                  onChange={handleAvailabilityChange}
                  disabled={loading || !isAvailableFromDateStatus}
                />
                {errors.avail_available_from && (
                  <span className="register-stage2-form__error-text">{errors.avail_available_from}</span>
                )}
              </div>

              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">Notice period (days) <span className="register-stage2-form__help-text">optional</span></label>
                <input
                  type="number"
                  name="notice_period_days"
                  className="register-stage2-form__input"
                  value={availabilityData.notice_period_days}
                  onChange={handleAvailabilityChange}
                  placeholder="30"
                  min="0"
                  disabled={loading || !isServingNoticeStatus}
                />
                {errors.avail_notice_period && (
                  <span className="register-stage2-form__error-text">{errors.avail_notice_period}</span>
                )}
              </div>

              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">Offshore willingness <span className="register-stage2-form__help-text">optional</span></label>
                <select
                  name="offshore_willingness"
                  className="register-stage2-form__select"
                  value={availabilityData.offshore_willingness}
                  onChange={handleAvailabilityChange}
                  disabled={loading}
                >
                  <option value="">Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
            </div>

            {/* Commercial - 3 or 4 columns */}
            <div className="register-stage2-form__grid register-stage2-form__grid--3col">
              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">Expected rate basis <span className="register-stage2-form__help-text">optional, never on CV</span></label>
                <select
                  name="rate_type"
                  className="register-stage2-form__select"
                  value={commercialData.rate_type}
                  onChange={handleCommercialChange}
                  disabled={loading || referenceValuesLoading}
                >
                  <option value="">{referenceValuesLoading ? "Loading rate types..." : "Select basis"}</option>
                  {rateTypeOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">Expected base rate <span className="register-stage2-form__help-text">optional, never on CV</span></label>
                <input
                  type="number"
                  name="expected_rate"
                  className="register-stage2-form__input"
                  value={commercialData.expected_rate}
                  onChange={handleCommercialChange}
                  placeholder="Amount"
                  min="0"
                  step="0.01"
                  disabled={loading}
                />
              </div>

              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">Currency</label>
                <select
                  name="currency"
                  className="register-stage2-form__select"
                  value={commercialData.currency}
                  onChange={handleCommercialChange}
                  disabled={loading || !commercialData.expected_rate}
                >
                  <option value="">Select currency</option>
                  {CURRENCY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Engagement Types - Full width */}
            <div className="register-stage2-form__grid register-stage2-form__grid--1col">
              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">
                  Preferred engagement type <span className="register-stage2-form__required">*</span>
                </label>
                <div className="register-stage2-checkbox-grid">
                  {referenceValuesLoading && (
                    <span className="register-stage2-form__help-text">Loading engagement types...</span>
                  )}
                  {engagementTypeOptions.map((opt) => (
                    <label key={opt.id} className="register-stage2-form__checkbox-wrapper">
                      <input
                        type="checkbox"
                        name="engagement_types"
                        value={opt.id}
                        checked={availabilityData.engagement_types.includes(String(opt.id))}
                        onChange={handleEngagementTypesChange}
                        disabled={loading}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
                {errors.avail_engagement_types && (
                  <span className="register-stage2-form__error-text">{errors.avail_engagement_types}</span>
                )}
              </div>
            </div>

            {/* Preferred Locations - Full width */}
            <div className="register-stage2-form__grid register-stage2-form__grid--1col">
              <div className="register-stage2-form__field-group">
                <label className="register-stage2-form__label">Preferred work locations <span className="register-stage2-form__help-text">optional</span></label>
                <div className="register-stage2-nationality-list">
                  {availabilityData.preferred_locations.map((location, index) => (
                    <div key={index} className="register-stage2-nationality-row">
                      <input
                        type="text"
                        className="register-stage2-form__input register-stage2-nationality-input"
                        value={location}
                        onChange={(e) => handlePreferredLocationChange(index, e.target.value)}
                        placeholder={index === 0 ? "India / GCC / Worldwide" : "Preferred location"}
                        disabled={loading}
                      />
                      {availabilityData.preferred_locations.length > 1 && (
                        <button
                          type="button"
                          className="register-stage2-form__btn register-stage2-form__btn--secondary"
                          onClick={() => removePreferredLocation(index)}
                          disabled={loading}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="register-stage2-add-btn register-stage2-add-btn--small"
                    onClick={addPreferredLocation}
                    disabled={loading}
                  >
                    + Add another location
                  </button>
                </div>
                <small className="register-stage2-form__help-text">
                  Add one or more preferred work locations.
                </small>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="register-stage2-form__actions">
        <div />
        <button
          type="button"
          className="register-stage2-form__btn register-stage2-form__btn--primary"
          onClick={onSubmit}
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit & Continue"}
        </button>
      </div>
    </div>
  );
};

export default Tab1PersonalProfile;